import { generateUniqueFilename } from "../utils/generateUniqueFilename";
import { API } from "./APIClient";
import qs from "qs";
import { getCurrentUserId } from "../utils/reduxUser";

class KnowledgeAPIClass {
    async fetchMaterialTests(materialsId) {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            materials_id: +materialsId,
            emp_id: userId,
        });

        const response = await API.get(`/knowledgeBase/materialTests?${query}`);

        const tests = []
        for (const item of response.data.tasks_list) {
            tests.push({
                id: item.id,
                groupId: item.group_id,
                name: item.name,
                rewardExp: item.reward_exp,
                isResolved: item.is_resolved,
                completedAt: new Date(item.completed_at),
            })
        }

        return {
            name: response.data.materials_name,
            tests
        };
    }

    async fetchMaterials() {
        const userId = getCurrentUserId();
        const response = await API.get(`/knowledgeBase/materials?employee_id=${userId}`);

        const result = [];
        for (const item of response.data.lst) {
            result.push({
                id: item.materials_id,
                title: item.name,
                icon: item.icon,
            })
        }

        return result;
    }

    async fetchTestById(materialsId, testId) {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            materials_id: +materialsId,
            test_id: +testId,
            emp_id: userId,
        });

        const response = await API.get(`/knowledgeBase/testsById?${query}`);

        return {
            id: response.data.id,
            name: response.data.name,
            rewardExp: response.data.reward_exp,
            text: response.data.text,
            answerVariants: response.data.answer_variants,
            canAnswer: response.data.can_answer,
        };
    }

    async fetchResolvedTests() {
        const userId = getCurrentUserId();

        const query = qs.stringify({
            emp_id: userId,
        });

        const response = await API.get(`/knowledgeBase/userResolvedTests?${query}`);
        return response.data;
    }

    async checkTestAnswer(testId, answerId) {
        try {
            const response = await API.post('/knowledgeBase/checkTestAnswer', {
                test_id: +testId,
                answer_id: +answerId
            })

            return response.data.message === "OK";

        } catch (err) {
            if (err.status === 400) {
                return false;
            }

            throw new Error(err);
        }
    }

    async createMaterial(materialName, cafeId = null) {
        const response = await API.post(`/sadmin/knowledgeBase/createMaterials`, {
            name: materialName,
            icon: "",
            cafe_id: cafeId,
        });

        const result = {
            materialId: response.data.materials_id,
            name: response.data.name,
            icon: response.data.icon,
            createdAt: new Date(response.data.created_at),
            cafeId: response.data.cafe_id,
        }

        return result;
    }

    async createTest(groupId, name, text, answerVariants, correctIndex, rewardExp) {
        const response = await API.post(`/sadmin/knowledgeBase/createTests`, {
            group_id: groupId,
            text: text,
            name: name,
            answer_variants: answerVariants,
            correct_idx: correctIndex,
            reward_exp: rewardExp,
        });

        const result = {
            id: response.data.id,
            groupId: response.data.group_id,
            text: response.data.text,
            name: response.data.name,
            answerVariants: response.data.answer_variants,
            correctIndex: response.data.correct_idx,
            rewardExp: response.data.reward_exp,
        }

        return result;
    }

    async deleteMaterial(materialId) {
        const response = await API.delete(`/sadmin/knowledgeBase/deleteMaterials?material_id=${materialId}`)

        return response.data;
    }

    async deleteTest(testId) {
        const response = await API.delete(`/sadmin/knowledgeBase/deleteTests?test_id=${testId}`)

        return response.data;
    }

    async fetchAdminMaterials(cafeId = null) {
        let response;
        if (cafeId) {
            response = await API.get(`/sadmin/knowledgeBase/materials?cafe_id=${cafeId}`);
        } else {
            response = await API.get(`/sadmin/knowledgeBase/materials`);
        }

        const result = [];
        for (const item of response.data.lst) {
            result.push({
                id: item.materials_id,
                title: item.name,
                icon: item.icon,
                cafeId: item.cafe_id,
            })
        }

        return result;
    }

    async fetchAdminTests(materialsId) {
        const response = await API.get(`/sadmin/knowledgeBase/materials/testsByMaterialsId?materials_id=${materialsId}`);

        const tests = []
        for (const item of response.data.tasks) {
            tests.push({
                id: item.id,
                groupId: item.group_id,
                name: item.name,
                rewardExp: item.reward_exp,
                isResolved: item.is_resolved,
                completedAt: new Date(item.completed_at),
            })
        }

        return {
            name: response.data.materials_name,
            cafeId: response.data.cafe_id,
            tests: tests
        };
    }

    async uploadMaterialIcon(cafeId, materialId, image) {
        const formData = new FormData();

        if (cafeId) {
            formData.append("cafe_id", cafeId);
        }

        formData.append("materials_id", materialId);
        formData.append("materials_image", image);
        formData.append("image_filename", generateUniqueFilename(image.name));

        const response = await API.post(`/sadmin/knowledgeBase/materials/uploadMaterialsImage`, formData);

        return {
            materialId: response.data.materials_id,
            name: response.data.name,
            icon: response.data.icon,
            createdAt: response.data.created_at,
            cafeId: response.data.cafe_id,
        }
    }

    async fetchMaterialIconURL(imagePath) {
        const response = await API.get(`/knowledgeBase/materialsImageUrl?image_path=${imagePath}`);

        return response.data.image_url;
    }

    async fetchEducationMaterials(categoryId) {
        const response = await API.get(`/sadmin/knowledgeBase/materials/educationalMaterials?material_id=${categoryId}`);

        const result = [];
        for (const item of response.data.materials) {
            result.push({
                id: item.id,
                title: item.title,
            })
        }

        return result;
    }

    async createEducationMaterial(categoryId, name, text) {
        const response = await API.post(`/sadmin/knowledgeBase/materials/createEducationalMaterial`, {
            material_id: categoryId,
            title: name,
            text: text,
        });

        return {
            id: response.data.id,
            materialId: response.data.material_id,
            title: response.data.title,
            text: response.data.text,
        };
    }

    async deleteEducationMaterial(materialId) {
        const response = await API.delete(`/sadmin/knowledgeBase/materials/deleteEducationalMaterial?educational_material_id=${materialId}`);

        return response.data;
    }

    async updateEducationMaterial(materialId, title, text) {
        const formData = new FormData();

        formData.append("educational_material_id", materialId);
        formData.append("title", title);
        formData.append("text", text);

        const response = await API.post(`/sadmin/knowledgeBase/materials/updateEducationalMaterial`, formData);

        return {
            id: response.data.id,
            categoryId: response.data.material_id,
            title: response.data.title,
            text: response.data.text,
        };
    }

    async addEducationMaterialImage(materialId, image) {
        const formData = new FormData();

        formData.append("educational_material_id", materialId);
        formData.append("image_file", image);
        formData.append("image_filename", generateUniqueFilename(image.name));

        const response = await API.post(`/sadmin/knowledgeBase/materials/addImageToEducationalMaterial`, formData);

        return {
            id: response.data.id,
            categoryId: response.data.material_id,
            s3FilenameKey: response.data.s3_filename_key,
        }
    }

    async deleteEducationMaterialImage(s3FilenameKey) {
        const response = await API.delete(`/sadmin/knowledgeBase/materials/deleteEducationalMaterialImage?educational_material_image_id=${s3FilenameKey}`);

        return response.data;
    }

    async updateTest(testId, name, text, answerVariants, correctAnswer, rewardExp) {
        const response = await API.put(`/sadmin/knowledgeBase/materials/updateTest`, {
            test_id: testId,
            name: name,
            text: text,
            answer_variants: answerVariants,
            correct_idx: correctAnswer,
            reward_exp: rewardExp,
        });

        return {
            testId: response.data.test_id,
            name: response.data.name,
            text: response.data.text,
            answerVariants: response.data.answer_variants,
            correctAnswer: response.data.correct_idx,
            rewardExp: response.data.reward_exp,
        }
    }

    async fetchEmployeeEducationMaterial(materialId) {
        const userId = getCurrentUserId();

        const response = await API.get(`/knowledgeBase/materials/educationalMaterial/${materialId}?employee_id=${userId}`);

        const images = [];
        for (const item of response.data.images) {
            images.push({
                id: item.image_id,
                src: item.image_url,
            })
        }

        return {
            id: response.data.id,
            categoryId: response.data.material_id,
            title: response.data.title,
            text: response.data.text,
            createdAt: response.data.created_at,
            images: images,
        }
    }

    async fetchEmployeeVideoUrl(videoId, expiration = 3600) {
        const userId = getCurrentUserId();
        const response = await API.get(`/knowledgeBase/videos/${videoId}/url?employee_id=${userId}&expiration=${expiration}`);
        return response.data.video_url;
    }

    async fetchEmployeeEducationMaterialVideos(materialId, expiration = 3600) {
        const userId = getCurrentUserId();
        const response = await API.get(`/knowledgeBase/materials/educationalMaterial/${materialId}/videos?employee_id=${userId}`);

        const videos = response.data.videos || [];

        const resolved = await Promise.all(
            videos.map(async (video) => {
                const videoUrl = await this.fetchEmployeeVideoUrl(video.id, expiration);
                return {
                    id: video.id,
                    educationalMaterialId: video.educational_material_id,
                    name: video.name,
                    description: video.description,
                    s3Filename: video.s3_filename,
                    src: videoUrl,
                };
            })
        );

        return resolved;
    }

    async fetchEmployeeEducationMaterials(categoryId) {
        const userId = getCurrentUserId();

        const response = await API.get(`/knowledgeBase/materials/${categoryId}/educationalMaterials?employee_id=${userId}`);

        const result = [];
        for (const item of response.data.materials) {
            result.push({
                id: item.id,
                title: item.title,
            })
        }

        return result;
    }

    async fetchEmployeeMaterialImageURL(imagePath) {
        const response = await API.get(`/knowledgeBase/materialsImageUrl?image_path=${imagePath}`);

        return response.data.image_url;
    }

    async fetchAdminEducationMaterial(materialId) {
        const response = await API.get(`/sadmin/knowledgeBase/materials/educationalMaterial/${materialId}`);

        const images = [];
        for (const item of response.data.images) {
            images.push({
                id: item.image_id,
                src: item.image_url,
            })
        }

        return {
            id: response.data.id,
            categoryId: response.data.material_id,
            title: response.data.title,
            text: response.data.text,
            images: images,
        };
    }

    async fetchAdminTest(testId) {
        const response = await API.get(`/sadmin/knowledgeBase/materials/tests/${testId}`);

        return {
            id: response.data.id,
            categoryId: response.data.group_id,
            name: response.data.name,
            text: response.data.text,
            answerVariants: response.data.answer_variants,
            correctAnswer: response.data.correct_idx,
            rewardExp: response.data.reward_exp,
        };
    }

    async updateCategoryName(categoryId, newName) {
        const response = await API.put(`/sadmin/knowledgeBase/materials/${categoryId}/updateMaterialsName?name=${newName}`);

        return response.data;
    }

    async changeCategoryVisibility(categoryId, target) {
        const response = await API.post(`/sadmin/knowledgeBase/materials/changeVisibility`, {
            materials_id: categoryId,
            cafe_id: target,
        });

        return response.data;
    }

    async changeOrder(categoryId, prevId, nextId) {
        const response = await API.put(`/sadmin/knowledgeBase/materials/updateOrder`, {
            materials_id: categoryId,
            prev_material_id: prevId,
            next_material_id: nextId,
        });

        return {
            id: response.data.materials_id,
            name: response.data.name,
            icon: response.data.icon,
            cafeId: response.data.cafe_id,
            order: response.data.order,
            createdAt: response.data.created_at,
        };
    }

    async uploadMaterialVideo(materialId, video) {
        const formData = new FormData();

        formData.append("name", video.name);
        formData.append("filename", video.name);
        formData.append("video_file", video);

        const response = await API.post(`/sadmin/knowledgeBase/educationalMaterial/${materialId}/videos`, formData);

        return {
            id: response.data.id,
            name: response.data.name,
            description: response.data.description,
            s3_filename: response.data.s3_filename
        };
    }

    async fetchAdminVideoUrl(videoId, expiration = 3600) {
        const response = await API.get(`/sadmin/knowledgeBase/videos/${videoId}/url?expiration=${expiration}`);

        return response.data.video_url;
    }

    async fetchAdminEducationMaterialVideos(materialId, expiration = 3600) {
        const response = await API.get(`/sadmin/knowledgeBase/educationalMaterial/${materialId}/videos`);

        const videos = response.data.videos || [];

        const resolved = await Promise.all(
            videos.map(async (video) => {
                const videoUrl = await this.fetchAdminVideoUrl(video.id, expiration);
                return {
                    id: video.id,
                    educationalMaterialId: video.educational_material_id,
                    name: video.name,
                    description: video.description,
                    s3Filename: video.s3_filename,
                    src: videoUrl,
                };
            })
        );

        return resolved;
    }

    async deleteEducationMaterialVideo(videoId) {
        const response = await API.delete(`/sadmin/knowledgeBase/videos/${videoId}`);
        return response.data;
    }
}

const KnowledgeAPI = new KnowledgeAPIClass();
export default KnowledgeAPI;
